import React, { createContext, useContext, useState, useCallback, useEffect } from "react";
import BlockedUsersService from "../../../services/helpers/BlockedUsersService";

interface BlockedUsersContextType {
  blockedUserIds: Set<string>;
  blockUser: (userId: string) => void;
  unblockUser: (userId: string) => void;
  isBlocked: (userId: string) => boolean;
}

const BlockedUsersContext = createContext<BlockedUsersContextType | null>(null);

export const BlockedUsersProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [blockedUserIds, setBlockedUserIds] = useState<Set<string>>(
    () => BlockedUsersService.getBlockedUsers()
  );

  // On mobile browsers (especially iOS Safari), navigating back restores the page from
  // bfcache with a frozen React state snapshot — meaning blocks made on another page won't
  // be reflected. Re-sync from localStorage whenever the page is restored from cache.
  useEffect(() => {
    const handlePageShow = (e: PageTransitionEvent) => {
      if (e.persisted) {
        setBlockedUserIds(new Set(BlockedUsersService.getBlockedUsers()));
      }
    };
    window.addEventListener("pageshow", handlePageShow);
    return () => window.removeEventListener("pageshow", handlePageShow);
  }, []);

  const blockUser = useCallback((userId: string) => {
    BlockedUsersService.blockUser(userId);
    setBlockedUserIds(new Set(BlockedUsersService.getBlockedUsers()));
  }, []);

  const unblockUser = useCallback((userId: string) => {
    BlockedUsersService.unblockUser(userId);
    setBlockedUserIds(new Set(BlockedUsersService.getBlockedUsers()));
  }, []);

  const isBlocked = useCallback(
    (userId: string) => blockedUserIds.has(userId),
    [blockedUserIds]
  );

  return (
    <BlockedUsersContext.Provider value={{ blockedUserIds, blockUser, unblockUser, isBlocked }}>
      {children}
    </BlockedUsersContext.Provider>
  );
};

export const useBlockedUsers = (): BlockedUsersContextType => {
  const ctx = useContext(BlockedUsersContext);
  if (!ctx) throw new Error("useBlockedUsers must be used within BlockedUsersProvider");
  return ctx;
};
